import * as React from 'react';
import FontAwesomeIcon from 'react-fontawesome';
import { useSelector } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import Modal from '@mui/material/Modal';
import { Store, Order, FinalizeOrderType } from './types';
import { getProductName, getTableCode } from './helpers';
import { OPEN_ORDER_PATH } from './Constants';
import * as Api from './Api';

const SCROLL_STEP = 40;

const OpenOrdersFooter: React.FC<RouteComponentProps> = ({ match }) => {
    const [modalOpen, setModalOpen] = React.useState(false);
    const [selectedOrder, setSelectedOrder] = React.useState<Order>(null);
    const tablesRef = React.useRef<HTMLDivElement>();

    const tables = useSelector(({ app }: Store) => app.tables);
    const notDispatchedOrders = useSelector(
        ({ app }: Store) => app.notDispatchedOrders
    );
    const products = useSelector(({ app }: Store) => app.products);

    const handleDialogClose = React.useCallback(() => {
        setModalOpen(false);
        setSelectedOrder(null);
    }, []);

    const handleFinalizeOrder = React.useCallback(
        (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
            e.stopPropagation();

            handleDialogClose();
            Api.finalizeOrder(selectedOrder.id, FinalizeOrderType.DeliverOrder);
        },
        [handleDialogClose, selectedOrder?.id]
    );

    const renderFullOrder = React.useCallback(
        () => (
            <div className='order container modal'>
                {selectedOrder && (
                    <>
                        <div className='table-id'>
                            <div>
                                Table {getTableCode(tables, selectedOrder.tableID)}
                            </div>
                            <FontAwesomeIcon
                                name='times-circle'
                                className='close-icon'
                                onClick={handleDialogClose}
                            />
                        </div>
                        <div className='order-items'>
                            {selectedOrder.orderItems.map(
                                ({
                                    id,
                                    quantity,
                                    productID,
                                    comments,
                                    productVarietyID
                                }) => {
                                    const variety = products
                                        .find(p => p.id === productID)
                                        ?.productVarieties?.find(
                                            v => v.id === productVarietyID
                                        );

                                    return (
                                        <div className='product' key={id}>
                                            <div className='left'>
                                                <div className='name'>
                                                    {getProductName(
                                                        products,
                                                        productID
                                                    )}
                                                </div>
                                                <div className='variety'>
                                                    {variety?.name}
                                                </div>
                                                {comments && (
                                                    <div className='comments'>
                                                        {comments}
                                                    </div>
                                                )}
                                            </div>
                                            <div className='quantity'>x{quantity}</div>
                                        </div>
                                    );
                                }
                            )}
                        </div>
                    </>
                )}
                <div className='actions'>
                    <button
                        className='finalize-order'
                        type='button'
                        onClick={handleFinalizeOrder}>
                        FINALIZE
                    </button>
                </div>
            </div>
        ),
        [handleDialogClose, handleFinalizeOrder, products, selectedOrder, tables]
    );

    const setSelectedOrderId = React.useCallback(
        (order: Order) => () => {
            setModalOpen(true);
            setSelectedOrder(order);
        },
        []
    );

    const handleScrollLeft = React.useCallback(() => {
        if (tablesRef.current) {
            tablesRef.current.scrollLeft -= SCROLL_STEP;
        }
    }, []);

    const handleScrollRight = React.useCallback(() => {
        if (tablesRef.current) {
            tablesRef.current.scrollLeft += SCROLL_STEP;
        }
    }, []);

    const renderTables = React.useCallback(
        () => (
            <>
                <div className='orders-footer'>
                    <FontAwesomeIcon
                        name='angle-left'
                        className='arrow'
                        onClick={handleScrollLeft}
                    />
                    <div className='tables' ref={tablesRef}>
                        {notDispatchedOrders.map(order => (
                            <div
                                className='table-code'
                                key={order.id}
                                onClick={setSelectedOrderId(order)}>
                                T-{getTableCode(tables, order.tableID)}
                            </div>
                        ))}
                    </div>
                    <FontAwesomeIcon
                        name='angle-right'
                        className='arrow'
                        onClick={handleScrollRight}
                    />
                </div>
                <Modal
                    open={modalOpen}
                    onClose={handleDialogClose}
                    BackdropProps={{ classes: { root: 'backdrop' } }}
                    classes={{ root: 'modal-dialog' }}>
                    {renderFullOrder()}
                </Modal>
            </>
        ),
        [
            handleDialogClose,
            handleScrollLeft,
            handleScrollRight,
            modalOpen,
            notDispatchedOrders,
            renderFullOrder,
            setSelectedOrderId,
            tables
        ]
    );

    return match.path === OPEN_ORDER_PATH ? renderTables() : null;
};

export default withRouter(OpenOrdersFooter);
